import { createSelector } from 'reselect';
import { RootState } from '@state/types';
import { getType } from 'typesafe-actions';
import * as settingsActions from './actions';
import { DateFormat } from 'shovel-lib/types';
import { initialUserSettings, initialOnboardingStatus } from './reducers';
import { now, timeDiffInDays, toMomentDate } from 'shovel-lib/utils/timeUtils';
import {
  repeatsDroppedCommitments,
  showsMarkUsedButtons,
  showsStudyStreak,
  showsStudyStreakDays,
  showsToStudyLabel,
  startAheadDays,
  startsAheadByTimeNeeded
} from '@utils/settingsUtils';

const getSubscriptionInfo = (state: RootState) => {
  return state.settings.subscription;
};

const getSubscriptionType = (state: RootState) => {
  if (!state.settings.subscription) return undefined;
  return state.settings.subscription.type;
};

const getUserSettings = (state: RootState) => state.settings.userSettings || initialUserSettings;

const hasSettings = (state: RootState) => !!state.settings.userSettings;

// One object for as long as the settings stay the same: the task sidebar hands it to every card, and a new one each time
// the store changed drew every card again.
const getPersonalizationSettings = createSelector([(state: RootState) => state.settings.userSettings], userSettings => {
  const {
    time24Format,
    dateFormat,
    startOfDay,
    firstDayOfWeek,
    daysAhead,
    showFlags,
    courseEventStyle,
    activityEventStyle,
    googleEventStyle,
    plannedTaskEventStyle,
    showCompletedTasks
  } = userSettings || initialUserSettings;
  return {
    time24Format,
    dateFormat,
    startOfDay,
    firstDayOfWeek,
    daysAhead,
    showFlags,
    courseEventStyle,
    activityEventStyle,
    googleEventStyle,
    plannedTaskEventStyle,
    showCompletedTasks,
    // Read through the helper, so settings saved before it existed show the ✓ discs.
    showMarkUsedButtons: showsMarkUsedButtons(userSettings),
    repeatDroppedCommitments: repeatsDroppedCommitments(userSettings),
    showStudyStreak: showsStudyStreak(userSettings),
    showStudyStreakDays: showsStudyStreakDays(userSettings),
    showToStudyLabel: showsToStudyLabel(userSettings),
    startAheadMode: startsAheadByTimeNeeded(userSettings) ? ('time' as const) : ('days' as const)
  };
});

const getOnboardingStatus = ({ settings }: RootState) => settings.userSettings?.onboardingStatus || initialOnboardingStatus;

const getDateFormat = ({ settings }: RootState) => settings.userSettings?.dateFormat || DateFormat.MONTH_FIRST;

const getSettingsLoading = ({ common }: RootState) => {
  const getSettingsType = getType(settingsActions.getUserSettings.request).split('_REQUEST')[0];
  return common.loaders[getSettingsType];
};

const getHelpUsDialogState = (state: RootState) => state.settings.helpUsDialog;

const isPlannedTimeIncludedInCushion = ({ semester }: RootState) =>
  semester.info && semester.info.user ? semester.info.user.includePlannedInCushion : false;

const getDaysTrialLeft = (state: RootState) => {
  const subscription = state.settings?.subscription;
  if (!subscription) return null;

  const trial = subscription.trial;
  if (!trial) return null;

  const currentTime = now();

  if (toMomentDate(subscription.accessUntil!).isBefore(currentTime)) {
    return null;
  }

  return Math.ceil(timeDiffInDays(currentTime.toDate(), subscription.accessUntil!));
};

const getPaymentProcessor = (state: RootState) => {
  // prevent returning null
  return state.settings?.subscription?.paymentProcessor || undefined;
};

const getUserBadges = ({ settings }: RootState) => settings.badgesInfo;

const getStepDialogInfo = ({ settings }: RootState) => settings.stepDialogInfo;

const getBadgesObviousMike = ({ settings }: RootState) => settings.badgesObviousMike;

const getShowOnboardingBar = ({ settings }: RootState) => settings.showOnboardingBar;

const getBagdesSteps = ({ settings }: RootState) => settings.badgesInfo?.steps;

const getOrganizationEmailDomainName = (state: RootState) => {
  return state.settings.subscription?.organizationEmailDomainName;
};

// Always a usable number of days, whatever was saved (see `startAheadDays`).
const getDefaultDaysAhead = (state: RootState) => {
  const { daysAhead } = state.settings.userSettings || initialUserSettings;
  return startAheadDays(daysAhead);
};

const getStartsAheadByTimeNeeded = (state: RootState) => startsAheadByTimeNeeded(state.settings.userSettings);

const getPersonalizationDialogInfo = (state: RootState) => {
  return {
    open: state.settings.personalizationDialog.open,
    info: state.settings.userSettings,
    callback: state.settings.personalizationDialog.callback
  };
};

const getPersonalizationDialogVisibility = (state: RootState) => {
  return state.settings.personalizationDialog.open;
};

export {
  getSubscriptionInfo,
  getSubscriptionType,
  getUserSettings,
  getPersonalizationSettings,
  getOnboardingStatus,
  getSettingsLoading,
  getDateFormat,
  getHelpUsDialogState,
  isPlannedTimeIncludedInCushion,
  getDaysTrialLeft,
  getPaymentProcessor,
  getUserBadges,
  getStepDialogInfo,
  getBadgesObviousMike,
  getShowOnboardingBar,
  getBagdesSteps,
  hasSettings,
  getOrganizationEmailDomainName,
  getDefaultDaysAhead,
  getStartsAheadByTimeNeeded,
  getPersonalizationDialogInfo,
  getPersonalizationDialogVisibility
};
